import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Camera, Plus, Search, Trash2, Wand2, Link2, Images, CheckCircle2 } from "lucide-react";
import { api, mediaUrl, errMsg } from "@/lib/galleryApi";
import { AccessBadge } from "@/components/gallery/accessModes";
import { CreateGalleryDialog } from "@/components/gallery/CreateGalleryDialog";
import { StudioAccessDialog } from "@/components/gallery/StudioAccessDialog";
import { ClientGalleriesList } from "@/components/gallery/ClientGalleriesList";
import { useStudioPerms } from "@/hooks/useStudioPerms";
import { ShieldCheck, FileArchive } from "lucide-react";
import { RestoreDialog } from "@/components/gallery/RestoreDialog";
import { PhaseBadge } from "@/components/gallery/retentionUi";

function GalleryCard({ g, onDelete }) {
  const navigate = useNavigate();
  const copy = () => {
    navigator.clipboard.writeText(`${window.location.origin}${g.share_url}`);
    toast.success("Lien copie");
  };
  return (
    <div className="group bg-[#0B1120] border border-[#1A2332] rounded-xl overflow-hidden hover:border-[#0099CC]/50 transition-colors" data-testid={`gallery-card-${g.id}`}>
      <button className="relative block w-full aspect-[16/10] bg-[#080E1A] overflow-hidden" onClick={() => navigate(`/app/galeries/${g.id}`)} data-testid={`open-gallery-${g.id}`}>
        {g.cover_url
          ? <img src={mediaUrl(g.cover_url)} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" loading="lazy" />
          : <Camera className="w-10 h-10 text-[#1A2332] absolute inset-0 m-auto" />}
        <div className="absolute top-2 left-2 flex gap-1.5"><AccessBadge mode={g.access_mode} />{g.is_restore_test ? <span className="text-[10px] bg-[#8B5CF6]/80 text-white px-2 py-0.5 rounded-full">Test de restauration</span> : <PhaseBadge r={g.retention} />}</div>
        {!g.is_active && <span className="absolute top-2 right-2 text-[10px] bg-red-500/80 text-white px-2 py-0.5 rounded-full">Desactivee</span>}
      </button>
      <div className="p-4">
        <h3 className="text-white font-semibold text-sm truncate">{g.title}</h3>
        <div className="flex items-center gap-3 mt-1.5 text-xs text-[#64748B]">
          <span className="flex items-center gap-1"><Images className="w-3 h-3" />{g.photo_count} photos</span>
          <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-[#10B981]" />{g.published_count} publiees</span>
        </div>
        <div className="flex items-center gap-1.5 mt-3">
          <Button size="sm" onClick={() => navigate(`/app/galeries/${g.id}`)} className="h-8 text-xs bg-[#0099CC]/10 text-[#0099CC] hover:bg-[#0099CC]/20">Gerer</Button>
          <Button size="sm" onClick={() => navigate(`/studio/${g.id}`)} className="h-8 text-xs bg-[#F97316]/10 text-[#F97316] hover:bg-[#F97316]/20" data-testid={`studio-gallery-${g.id}`}><Wand2 className="w-3 h-3 mr-1" />Studio</Button>
          {g.access_mode !== "private" && (
            <Button size="sm" variant="ghost" onClick={copy} className="h-8 text-[#94A3B8] hover:text-white" data-testid={`share-gallery-${g.id}`}><Link2 className="w-3.5 h-3.5" /></Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => onDelete(g)} className="h-8 ml-auto text-[#64748B] hover:text-red-400" data-testid={`delete-gallery-${g.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
        </div>
      </div>
    </div>
  );
}

export default function GalleriesPage() {
  const perms = useStudioPerms();
  if (!perms) return <div className="min-h-screen bg-[#060B14]" />;
  if (perms.studio) return <StudioGalleries perms={perms} />;
  if (perms.client) return <ClientGalleriesList />;
  return <div className="min-h-screen bg-[#060B14] flex items-center justify-center text-sm text-[#94A3B8]" data-testid="galleries-forbidden">Acces non autorise</div>;
}

function StudioGalleries({ perms }) {
  const [showAccess, setShowAccess] = useState(false);
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [showRestore, setShowRestore] = useState(false);

  useEffect(() => {
    api.get("/galleries").then(setGalleries).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
  }, []);

  const confirmDelete = async () => {
    try {
      await api.del(`/galleries/${toDelete.id}`);
      setGalleries((prev) => prev.filter((g) => g.id !== toDelete.id));
      toast.success("Galerie supprimee — effacement des fichiers en arriere-plan");
    } catch (e) { toast.error(errMsg(e)); }
    setToDelete(null);
  };

  const filtered = galleries.filter((g) => g.title.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="min-h-screen bg-[#060B14] text-white" data-testid="galleries-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#0099CC] font-semibold mb-2">Studio Photo & Galeries</p>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Galeries</h1>
            <p className="text-[#64748B] text-sm mt-2 max-w-xl">Importez, selectionnez, traitez et partagez les photographies de vos evenements.</p>
          </div>
          <div className="flex gap-2">
            {perms.super_admin && <Button variant="outline" onClick={() => setShowAccess(true)} className="bg-transparent border-[#1A2332] text-[#94A3B8] h-10" data-testid="studio-access-btn"><ShieldCheck className="w-4 h-4 mr-2" />Acces Studio</Button>}
            <Button variant="outline" onClick={() => setShowRestore(true)} className="bg-transparent border-[#F97316]/40 text-[#F97316] h-10" data-testid="galleries-restore-btn"><FileArchive className="w-4 h-4 mr-2" />Restaurer une galerie</Button>
            <Button onClick={() => setShowCreate(true)} className="bg-[#0099CC] hover:bg-[#0088B5] text-white h-10" data-testid="create-gallery-btn"><Plus className="w-4 h-4 mr-2" />Nouvelle galerie</Button>
          </div>
        </div>
        <div className="relative mb-6 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher une galerie" className="pl-10 bg-[#0B1120] border-[#1A2332] text-white h-10" data-testid="gallery-search" />
        </div>
        {loading ? (
          <div className="flex justify-center py-24"><div className="w-8 h-8 border-2 border-[#0099CC]/30 border-t-[#0099CC] rounded-full animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-[#1A2332] rounded-2xl py-20 text-center" data-testid="galleries-empty">
            <Camera className="w-12 h-12 text-[#1A2332] mx-auto mb-4" />
            <h3 className="font-medium mb-1">Aucune galerie</h3>
            <p className="text-[#64748B] text-sm mb-6">Creez une galerie pour importer les photos de votre evenement.</p>
            <Button onClick={() => setShowCreate(true)} className="bg-[#0099CC] hover:bg-[#0088B5] text-white"><Plus className="w-4 h-4 mr-2" />Creer une galerie</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="galleries-grid">
            {filtered.map((g) => <GalleryCard key={g.id} g={g} onDelete={setToDelete} />)}
          </div>
        )}
      </div>
      <StudioAccessDialog open={showAccess} onOpenChange={setShowAccess} />
      <RestoreDialog open={showRestore} onOpenChange={setShowRestore} />
      <CreateGalleryDialog open={showCreate} onOpenChange={setShowCreate} isAdmin scopeOrgIds={perms.all_orgs ? null : perms.org_ids} onCreated={(g) => setGalleries((p) => [{ ...g, photo_count: 0, published_count: 0 }, ...p])} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-[#0B1120] border-[#1A2332] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer « {toDelete?.title} » ?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#94A3B8]">Suppression definitive : la galerie, les liens de partage et tous les fichiers (originaux, miniatures, versions retouchees) seront effaces du stockage. Action irreversible.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#1A2332] text-[#94A3B8]">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-gallery">Supprimer definitivement</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
